/**
 * The M21 enemy abilities (docs/ENEMIES_V2.md): the Summoner's imps, the Burrower's dives, the Spellward's magic resist
 * and the Stunner's shockwave. Numbers live on the archetype (`EnemyArchetype.abilities`, enemies.json).
 */
import { DamageType } from "../../data/balance";
import { EnemyAbilities } from "../../data/enemyAbilities";
import { Fix64 } from "../../maths/fix64";
import { distanceSquared, positionAt } from "../../world/roadGeometry";
import { SimConstants } from "../constants";
import { Enemy, EnemyAct, Tower } from "../entities";
import { SimEventKind } from "../events";
import type { LevelSim } from "../levelSim";
import { clearBurns, clearTickStatuses } from "./enemyMotion";
import { piercesWard } from "../branches/hits";

const HUNDRED = 100;
/** The highest base tier: above it a tower fights as its tier-4 branch (sim/branches). */
const TIER_THREE = 3;

/** Once per wave tick, right after movement: casts, dives and wind-ups advance; imps appear; shockwaves land. */
export function tickEnemyAbilities(sim: LevelSim): void {
  for (const e of sim.enemySlots) {
    if (!e.alive) continue;
    const k = sim.balance.enemies[e.archetype].abilities;
    // Tier 4: a stunned, frozen, rooted or polymorphed raider does nothing — a cast or wind-up it was in breaks off
    // (no imps, no shockwave; its cooldown starts over) and its other clocks wait. Zero on a run with no branch.
    if (e.stunTicksLeft > 0 || e.sheepTicksLeft > 0) { if (k.summonEnemy >= 0 || k.stompRadius.isPositive()) interrupt(sim, e, k); continue; }
    if (k.summonEnemy >= 0) tickSummoner(sim, e, k);
    else if (k.burrowDistance.isPositive()) tickBurrower(sim, e, k);
    else if (k.stompRadius.isPositive()) tickStunner(sim, e, k);
  }
}

/** A Summoner's cast or a Stunner's wind-up broken off by a stun or Polymorph (CastInterrupted); its cooldown starts over. */
function interrupt(sim: LevelSim, e: Enemy, k: EnemyAbilities): void {
  if (e.act !== EnemyAct.Summoning && e.act !== EnemyAct.WindingUp) return;
  sim.events.add(SimEventKind.CastInterrupted, e.id, e.act);
  e.abilityCooldown = e.act === EnemyAct.Summoning ? k.summonIntervalTicks : k.stompIntervalTicks;
  e.act = EnemyAct.None; e.actTicks = 0;
}

/** Casting, winding up, digging in or climbing out: the enemy stands still on the road this tick. */
export function standsStill(e: Enemy): boolean {
  return e.act === EnemyAct.Summoning || e.act === EnemyAct.WindingUp || e.act === EnemyAct.Digging || e.act === EnemyAct.Surfacing;
}

/** A burrowed enemy's step: × its burrow speed (mud does not slow it underground; enemy movement skips the mud). */
export function burrowStep(sim: LevelSim, e: Enemy, step: Fix64): Fix64 {
  return e.burrowed ? step.mul(sim.balance.enemies[e.archetype].abilities.burrowSpeedMult) : step;
}

/**
 * Burrower: a hit from a tower (not a burn, not a Barracks soldier's blade, not a fortress troop) while it walks the
 * surface, is not held, is off cooldown and has dives left makes it dig in. Called from applyDamage after the damage.
 */
export function onHitByTower(sim: LevelSim, e: Enemy, source: Tower | null, type: DamageType): void {
  // Not a burn tick, nor tier 4's periodic damage (poison, zones, auras, spiders, the phoenix: sim.shot.periodic), nor
  // while stunned or a sheep (sim/branches).
  if (source === null || type === DamageType.Burn || !e.alive || sim.shot.periodic || e.stunTicksLeft > 0 || e.sheepTicksLeft > 0) return;
  const k = sim.balance.enemies[e.archetype].abilities;
  if (!k.burrowDistance.isPositive() || e.act !== EnemyAct.None || e.blocked || e.abilityCooldown > 0 || e.burrowsUsed >= k.burrowMax) return;
  if (sim.balance.towers[source.archetype].soldiers > 0) return;
  e.act = EnemyAct.Digging; e.actTicks = Math.max(1, k.burrowDigTicks);
  sim.events.add(SimEventKind.EnemyDigging, e.id, e.actTicks);
}

/** Spellward: magic damage × its `magicTaken` (MagicResisted marks the hit for the rune shield). Other types pass. */
export function resistedDamage(sim: LevelSim, e: Enemy, source: Tower | null, damage: Fix64, type: DamageType): Fix64 {
  if (type !== DamageType.Magic) return damage;
  const taken = sim.balance.enemies[e.archetype].abilities.magicTaken;
  if (taken.eq(Fix64.One)) return damage;
  if (source !== null && source.tier > TIER_THREE && piercesWard(sim, source)) return damage;   // Mana Burn (tier 4, Sorcerer)
  const out = damage.mul(taken);
  sim.events.add(SimEventKind.MagicResisted, e.id, source?.id ?? 0, out.floorToInt());
  return out;
}

function tickSummoner(sim: LevelSim, e: Enemy, k: EnemyAbilities): void {
  if (e.abilityCooldown < 0) e.abilityCooldown = k.summonFirstDelayTicks;
  if (e.act === EnemyAct.Summoning) {
    if (--e.actTicks > 0) return;
    e.act = EnemyAct.None;
    e.abilityCooldown = k.summonIntervalTicks;
    summonImps(sim, e, k);
    return;
  }
  if (e.abilityCooldown > 0) { e.abilityCooldown--; return; }
  // At its cap it does not stop: it looks again after a cast's length.
  if (impsOf(sim, e) >= k.summonCap) { e.abilityCooldown = Math.max(1, k.summonCastTicks); return; }
  e.act = EnemyAct.Summoning; e.actTicks = Math.max(1, k.summonCastTicks);
  sim.events.add(SimEventKind.SummonStarted, e.id, e.actTicks);
}

/** The living imps this summoner called. */
export function impsOf(sim: LevelSim, summoner: Enemy): number {
  let n = 0;
  for (const x of sim.enemySlots) if (x.alive && x.summonerId === summoner.id) n++;
  return n;
}

/**
 * Up to `summonCount` imps (never past `summonCap` alive) appear behind the summoner, `summonSpacing` cells apart along
 * the road, at the stage power it spawned at, plain, worth their own points. A full enemy pool drops the rest.
 */
function summonImps(sim: LevelSim, s: Enemy, k: EnemyAbilities): void {
  const n = Math.min(k.summonCount, k.summonCap - impsOf(sim, s));
  const a = sim.balance.enemies[k.summonEnemy];
  let called = 0;
  for (let i = 0; i < n; i++) {
    const e = sim.enemySlots.find((x) => !x.alive);
    if (e === undefined) break;
    e.id = sim.nextEnemyId++;
    e.archetype = a.index; e.alive = true; e.isAir = a.isAir; e.isBoss = false; e.isRare = false; e.affix = -1;
    e.power = s.power;
    e.maxHp = a.hp.mul(s.power); e.hp = e.maxHp;
    e.speedPerTick = a.speedCellsPerSecond.divInt(SimConstants.TicksPerSecond);
    e.armour = a.armour;
    e.distance = Fix64.max(Fix64.Zero, s.distance.sub(k.summonSpacing.mulInt(i + 1)));
    e.points = a.points; e.leakDamage = a.leakDamage;
    e.slowFraction = Fix64.Zero; e.slowTicksLeft = 0;
    clearBurns(e);
    clearTickStatuses(e);
    e.shredStacks = 0; e.shredArmour = Fix64.Zero; e.boostTicksLeft = 0; e.boostCooldownLeft = 0; e.hitsTaken = 0;
    e.tolled = s.tolled; e.attackCooldown = 0; e.shieldArmour = Fix64.Zero;
    e.summonerId = s.id;
    [e.x, e.y] = positionAt(sim.road.roadCells, e.distance);
    e.cx = e.x.floorToInt(); e.cy = e.y.floorToInt();
    sim._aliveEnemies++;
    sim.events.add(SimEventKind.EnemySpawned, e.id, e.archetype, 0);
    called++;
  }
  sim.events.add(SimEventKind.ImpsSummoned, s.id, called);
}

function tickBurrower(sim: LevelSim, e: Enemy, k: EnemyAbilities): void {
  switch (e.act) {
    case EnemyAct.Digging:
      if (--e.actTicks > 0) return;
      e.act = EnemyAct.Burrowed;
      e.burrowsUsed++;
      e.surfaceAt = Fix64.min(e.distance.add(k.burrowDistance), sim.road.length);
      sim.events.add(SimEventKind.EnemyBurrowed, e.id, e.burrowsUsed, k.burrowDistance.mulInt(HUNDRED).floorToInt());
      return;
    case EnemyAct.Burrowed:
      if (e.distance.lt(e.surfaceAt)) return;
      e.act = EnemyAct.Surfacing; e.actTicks = Math.max(1, k.burrowSurfaceTicks);
      sim.events.add(SimEventKind.EnemySurfaced, e.id, e.actTicks);
      return;
    case EnemyAct.Surfacing:
      if (--e.actTicks > 0) return;
      e.act = EnemyAct.None;
      e.abilityCooldown = k.burrowCooldownTicks;
      return;
    default:
      if (e.abilityCooldown > 0) e.abilityCooldown--;
  }
}

function tickStunner(sim: LevelSim, e: Enemy, k: EnemyAbilities): void {
  if (e.abilityCooldown < 0) e.abilityCooldown = k.stompFirstDelayTicks;
  if (e.act === EnemyAct.WindingUp) {
    if (--e.actTicks > 0) return;
    e.act = EnemyAct.None;
    e.abilityCooldown = k.stompIntervalTicks;
    stomp(sim, e, k);
    return;
  }
  if (e.abilityCooldown > 0) { e.abilityCooldown--; return; }
  // It winds up only with a tower in reach; otherwise it walks on, ready.
  if (towersInReach(sim, e, k.stompRadius).length === 0) return;
  e.act = EnemyAct.WindingUp; e.actTicks = Math.max(1, k.stompWindupTicks);
  sim.events.add(SimEventKind.StompWindup, e.id, e.actTicks, k.stompRadius.mulInt(HUNDRED).floorToInt());
}

/** Every working-or-not tower (the fortress included) whose cell centre is within `radius` of the enemy, in slot order. */
export function towersInReach(sim: LevelSim, e: Enemy, radius: Fix64): Tower[] {
  const r2 = radius.mul(radius);
  const out: Tower[] = [];
  for (const t of sim.towerSlots) {
    if (t.alive && distanceSquared(Fix64.fromInt(t.cell.x), Fix64.fromInt(t.cell.y), e.x, e.y).le(r2)) out.push(t);
  }
  return out;
}

/** The shockwave: every tower in reach is disabled for `stompDisableTicks` (a longer disable already on it stands). */
function stomp(sim: LevelSim, e: Enemy, k: EnemyAbilities): void {
  const hit = towersInReach(sim, e, k.stompRadius);
  sim.events.add(SimEventKind.EnemyStomped, e.id, hit.length, k.stompRadius.mulInt(HUNDRED).floorToInt());
  for (const t of hit) {
    t.disabledTicksLeft = Math.max(t.disabledTicksLeft, k.stompDisableTicks);
    sim.events.add(SimEventKind.TowerDisabled, t.id, k.stompDisableTicks, e.id);
  }
}
